import React, { useEffect, useState } from "react";

// MUI
import { DataGridPro, GridPagination } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Me
import Container from '@mui/material/Container';
import { getJobs, runJob, createJob, deleteJob } from '../firebase';
import useJobsPolling from '../hooks/useJobsPolling';
import { 
  formatRelativeTime, 
  parseCronExpression, 
  getStatusInfo, 
  calculateDuration,
  formatDateTime 
} from '../utils';
import VariablesModal from "./VariablesModal";
import NewJobModal from "./NewJobModal";
import DeleteJobDialog from "./DeleteJobDialog";


export default function BasicTable({ accountDetails }) {
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const showSnackbar = (message, severity = 'info') => setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = (_e, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(s => ({ ...s, open: false }));
  };

  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, jobId: '' });
  const handleDeleteClick = (jobId) => setDeleteDialog({ open: true, jobId });
  const handleDeleteCancel = () => setDeleteDialog({ open: false, jobId: '' });
  const handleDeleteConfirm = async () => {
    const jobId = deleteDialog.jobId;
    setDeleteDialog({ open: false, jobId: '' });
    try {
      await deleteJob(jobId);
      showSnackbar(`Job ${jobId} deleted successfully`, 'success');
      fetchData();
    } catch (error) {
      showSnackbar(`Failed to delete job: ${error.message}`, 'error');
    }
  };
  
  // Run handler
  const handleRun = async (row) => {
    try {
      await runJob(row.jobName, row.account);
      showSnackbar(`Job ${row.jobName} triggered successfully!`, 'success');
      // Refresh the data after running
      fetchData();
    } catch (error) {
      showSnackbar(`Failed to run job: ${error.message}`, 'error');
    }
  };

  const [jobs, setJobs] = useState([]);
  const [newJobOpen, setNewJobOpen] = useState(false);
  
  // Modal control
  const [modalData, setModalData] = useState();
  const handleCloseModal = () => {
    setModalData(null);
  };

  const fetchData = async () => {
    try {
      const data = await getJobs();
      setJobs(data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      showSnackbar('Failed to load jobs. Please refresh the page.', 'error');
    }
  };

  // Poll for updates every 60s and on visibility change
  useJobsPolling({ onData: setJobs, intervalMs: 60000, enabled: true });

  // Ensure an immediate load on mount regardless of polling timing
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Responsive column visibility for DataGrid
  const theme = useTheme();
  const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));
  const isLgOnly = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
  const isMdOnly = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isSmOnly = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  useEffect(() => {
    if (isXlUp) {
      setColumnVisibilityModel({});
    } else if (isLgOnly) {
      setColumnVisibilityModel({
        lastRun: false,
      });
      } else if (isMdOnly) {
        setColumnVisibilityModel({
          lastRun: false,
          nextRun: false,
        });
    } else if (isSmOnly) {
      setColumnVisibilityModel({
        lastRun: false,
        nextRun: false,
        schedule: false,
        status: false,
      });
    } else if (isXs) {
      setColumnVisibilityModel({
        lastRun: false,
        nextRun: false,
        schedule: false,
        status: false,
      });
    }
  }, [isXlUp, isLgOnly, isMdOnly, isSmOnly, isXs]);

  // Detail panel renderer
  const getDetailPanelContent = ({ row }) => {
    const execution = row.latestExecution;
    const schedule = row.schedule;
    const duration = execution ? calculateDuration(execution.createTime, execution.completionTime) : null;

    // Build sections dynamically so we can add separators only between visible sections
    const sections = [];

    if (execution) {
      sections.push({
        key: 'execution',
        content: (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main', textTransform: 'uppercase' }}>
              LAST RUN
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">Started:</Typography>
              <Typography variant="body2">{formatDateTime(execution.createTime)}</Typography>
              <Typography variant="body2" color="text.secondary">Completed:</Typography>
              <Typography variant="body2">{formatDateTime(execution.completionTime)}</Typography>
              {duration && (
                <>
                  <Typography variant="body2" color="text.secondary">Duration:</Typography>
                  <Typography variant="body2">{duration}</Typography>
                </>
              )}
              <Typography variant="body2" color="text.secondary">Status:</Typography>
              <Typography variant="body2">{execution.completionStatus}</Typography>
            </Box>
          </>
        ),
      });
    }

    if (schedule) {
      sections.push({
        key: 'schedule',
        content: (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main', textTransform: 'uppercase' }}>
              SCHEDULE
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 1 }}>
              <Typography variant="body2" sx={{ gridColumn: '1 / -1' }}>
                <Typography component="span" variant="body2" color="text.secondary">Cron:</Typography>{' '}
                <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {schedule.schedule} ({parseCronExpression(schedule.schedule)})
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">Timezone:</Typography>
              <Typography variant="body2">{schedule.timeZone}</Typography>
              <Typography variant="body2" color="text.secondary">Next Run:</Typography>
              <Typography variant="body2">
                {schedule.nextScheduledTime 
                  ? `${formatDateTime(schedule.nextScheduledTime)} (${formatRelativeTime(schedule.nextScheduledTime)})`
                  : '-'
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">State:</Typography>
              <Box>
                <Chip 
                  label={schedule.state} 
                  size="small"
                  color={schedule.state === 'ENABLED' ? 'success' : 'default'}
                />
              </Box>
              {schedule.lastAttemptTime && (
                <>
                  <Typography variant="body2" color="text.secondary">Last Attempt:</Typography>
                  <Typography variant="body2">{formatDateTime(schedule.lastAttemptTime)}</Typography>
                </>
              )}
            </Box>
          </>
        ),
      });
    }

    sections.push({
      key: 'info',
      content: (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
            INFO
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Created:</Typography>
            <Typography variant="body2">{formatDateTime(row.createTime)}</Typography>
            <Typography variant="body2" color="text.secondary">Updated:</Typography>
            <Typography variant="body2">{formatDateTime(row.updateTime)}</Typography>
          </Box>
        </>
      ),
    });

    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3} alignItems="stretch">
          {sections.map((section, idx) => {
            const isLast = idx === sections.length - 1;
            return (
              <Grid key={section.key} item xs={12} md={4} sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    pr: { xs: 0, md: isLast ? 0 : 3 },
                    mr: { xs: 0, md: isLast ? 0 : 3 },
        borderRight: { xs: 'none', md: isLast ? 'none' : '1px solid' },
                    borderColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
                  }}
                >
                  {section.content}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const getDetailPanelHeight = () => 'auto';

  if (jobs.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </div>
    );
  }

  const columns = [
    { 
      field: 'jobName', 
      headerName: 'Job Name', 
      flex: 1,
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const status = params.row.latestExecution?.completionStatus;
        console.log(status)
        const statusInfo = getStatusInfo(status);
        return (
          <Chip
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{statusInfo.icon}</span>
                <span>{statusInfo.label}</span>
              </Box>
            }
            size="small"
            sx={{ 
              backgroundColor: `${statusInfo.color}20`,
              color: statusInfo.color,
              fontWeight: 500
            }}
          />
        );
      }
    },
    { 
      field: 'lastRun', 
      headerName: 'Last Run', 
      flex: 1,
      valueGetter: (_value, row) => {
        return row.latestExecution?.completionTime || null;
      },
      renderCell: (params) => {
        const completionTime = params.row.latestExecution?.completionTime;
        return formatRelativeTime(completionTime);
      }
    },
    { 
      field: 'schedule', 
      headerName: 'Schedule', 
      flex: 1,
      valueGetter: (_value, row) => {
        return row.schedule?.schedule || null;
      },
      renderCell: (params) => {
        const schedule = params.row.schedule?.schedule;
        return parseCronExpression(schedule);
      }
    },
    { 
      field: 'nextRun', 
      headerName: 'Next Run', 
      flex: 1,
      valueGetter: (_value, row) => {
        return row.schedule?.nextScheduledTime || null;
      },
      renderCell: (params) => {
        const nextRun = params.row.schedule?.nextScheduledTime;
        return formatRelativeTime(nextRun);
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const vars = params.row?.variables;
        const hasVariables = !!vars && (
          (Array.isArray(vars) && vars.length > 0) ||
          (typeof vars === 'object' && vars !== null && Object.keys(vars).length > 0)
        );
        const isDeletable = params.row?.deletable === true;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
            <Button 
              onClick={() => handleRun(params.row)}
              variant="outlined"
              size="small"
            >
              Run
            </Button>
            {hasVariables && (
              <Button 
                onClick={() => setModalData(params.row)}
                variant="outlined"
                size="small"
              >
                Variables
              </Button>
            )}
            {isDeletable && (
              <IconButton 
                onClick={() => handleDeleteClick(params.row.id)}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      },
    },
  ];

  const rows = [...jobs]
    .sort((a, b) => (a.jobName || '').localeCompare(b.jobName || '', undefined, { sensitivity: 'base' }))
    .map((job) => ({
      id: job.jobId,
      ...job
    }));

  const CustomFooter = () => (
    // <Box role="contentinfo" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
    <Box role="contentinfo" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setNewJobOpen(true)}>
        New Job
      </Button>
      <GridPagination />
    </Box>
  );

  // No top toolbar; action lives in the footer

  return (
    <Container maxWidth={'xl'}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Jobs</Typography>
      </Box>
      <DataGridPro
        rows={rows}
        columns={columns}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        autoHeight
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        slots={{ footer: CustomFooter }}
      />    
    {modalData && (
      <VariablesModal
        open={!!modalData}
        onClose={handleCloseModal}
        data={modalData}
      />
    )}
    <NewJobModal
      open={newJobOpen}
      onClose={() => setNewJobOpen(false)}
      onCreate={async (payload) => {
        try {
          await createJob(payload);
          setNewJobOpen(false);
          await fetchData();
          showSnackbar('Job created successfully', 'success');
        } catch (err) {
          showSnackbar(`Failed to create job: ${err.message}`, 'error');
        }
      }}
      accountDetails={accountDetails}
    />
    <DeleteJobDialog
      open={deleteDialog.open}
      jobName={deleteDialog.jobName}
      onCancel={handleDeleteCancel}
      onConfirm={handleDeleteConfirm}
    />
    <Snackbar
      open={snackbar.open}
      autoHideDuration={4000}
      onClose={handleSnackbarClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
        {snackbar.message}
      </Alert>
    </Snackbar>
    </Container>
  );
}
